import { SBContact } from "@smart/bridge-smokeball-basic";
import { loadDefaultCountry } from "@smart/itops-locale-dom";
import { TruncatedTooltip } from "@smart/itops-sb-design-system-dom";
import { specialChars } from "@smart/itops-utils-basic";

import { formatAddressLine, formatPhoneNumber } from "./utils";

export const ContactDetails = ({
  contact,
  compareToContact,
}: {
  contact: Omit<SBContact, "id"> & { id?: string };
  compareToContact?: Omit<SBContact, "id"> & { id?: string };
}) => {
  const isPerson = !!(contact.person && Object.keys(contact.person).length);
  const mobileNumber = formatPhoneNumber(contact.person?.cell);
  const homeNumber = formatPhoneNumber(contact.person?.phone);
  const companyNumber = formatPhoneNumber(contact.company?.phone);
  const email = contact.person?.email || contact.company?.email;
  const address =
    contact.person?.residentialAddress || contact.company?.businessAddress;
  const defaultCountry = loadDefaultCountry();
  const addressLine = address && formatAddressLine(address, defaultCountry);

  const headingClassNames = "font-semibold text-neutral-600";
  const detailClassNames = "flex flex-col gap-[0.4rem]";

  const compareAddress =
    compareToContact &&
    (compareToContact.person?.residentialAddress ||
      compareToContact.company?.businessAddress);

  const compareValues = compareToContact
    ? {
        mobileNumber: formatPhoneNumber(compareToContact.person?.cell),
        homeNumber: formatPhoneNumber(compareToContact.person?.phone),
        companyNumber: formatPhoneNumber(compareToContact.company?.phone),
        email:
          compareToContact.person?.email || compareToContact.company?.email,
        address:
          compareAddress &&
          formatAddressLine(compareAddress, loadDefaultCountry()),
        state: compareAddress?.state,
        country: compareAddress?.country,
      }
    : undefined;

  const contactDetails = (
    heading: string,
    value?: string,
    compareValue?: string | null,
  ) => (
    <div className={detailClassNames}>
      <div className={headingClassNames}>{heading}</div>
      <div className="flex items-center gap-small">
        <TruncatedTooltip
          tooltipText={value ?? undefined}
          textToDisplay={value || specialChars.enDash}
          className="max-w-[20rem] truncate"
          dataTestId={`contact-${heading}`}
          offsetBy={10}
        />
        {compareValues && compareValue !== value && (
          <div className="h-[0.8rem] w-[0.8rem] rounded-full bg-yellow-450" />
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-medium">
      {isPerson ? (
        <>
          {contactDetails("Phone:", homeNumber, compareValues?.homeNumber)}
          {contactDetails(
            defaultCountry === "US" ? "Cell:" : "Mobile:",
            mobileNumber,
            compareValues?.mobileNumber,
          )}
        </>
      ) : (
        contactDetails("Phone:", companyNumber, compareValues?.companyNumber)
      )}
      {contactDetails("Email:", email ?? undefined, compareValues?.email)}
      {contactDetails(
        "Address:",
        addressLine ?? undefined,
        compareValues?.address,
      )}
    </div>
  );
};
